import { Navigate, Outlet, useLocation } from "react-router-dom";
import { usePermissions } from "../helper/PermissionContext";

// Route path (matched by first path segment) -> permission slug required to
// open it. Add/edit sub-routes inherit their parent module's permission.
const ROUTE_PERMISSIONS = {
  "/": "dashboard",
  "/dynamic-page": "dynamic-page",
  "/add-dynamic-page": "dynamic-page",
  "/edit-dynamic-page": "dynamic-page",
  "/author": "blog-management",
  "/category": "blog-management",
  "/tag": "blog-management",
  "/blog": "blog-management",
  "/add-blog": "blog-management",
  "/edit-blog": "blog-management",
  "/seo": "seo-management",
  "/add-seo": "seo-management",
  "/edit-seo": "seo-management",
  "/news": "news-management",
  "/add-news": "news-management",
  "/edit-news": "news-management",
  "/branch": "branch-management",
  "/add-branch": "branch-management",
  "/edit-branch": "branch-management",
  "/faq": "faq-management",
  "/add-faq": "faq-management",
  "/edit-faq": "faq-management",
  "/sitemap": "sitemap",
  "/global-seo": "global-seo",
  "/schema-management": "global-seo",
  "/llms": "llms-management",
  "/robots": "robots-management",
  "/email-management": "email-management",
  "/add-email-management": "email-management",
  "/edit-email-management": "email-management",
  "/disclosure": "mandatory-disclosure",
  "/add-disclosure": "mandatory-disclosure",
  "/edit-disclosure": "mandatory-disclosure",
  "/contact": "contact-us",
  "/schedule-visit": "schedule-a-visit",
  "/apply-now": "apply-now",
  "/career": "career",
  // "/users": "user-management",
  // "/roles": "user-management",
  // "/permissions": "user-management",
};

// Match by path segments so "/faq" never matches "/add-faq" when not in the map.
const permissionForPath = (pathname) => {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return ROUTE_PERMISSIONS["/"];
  for (const [key, slug] of Object.entries(ROUTE_PERMISSIONS)) {
    if (key === "/") continue;
    const keySegments = key.split("/").filter(Boolean);
    if (keySegments.every((segment, index) => segments[index] === segment)) return slug;
  }
  return null; // unknown route -> no restriction
};

const AccessDenied = () => (
  <div
    style={{
      padding: "60px 20px",
      textAlign: "center",
      fontFamily: "inherit",
    }}
  >
    <h3 style={{ margin: "0 0 10px", fontSize: "22px" }}>Access Denied</h3>
    <p style={{ margin: 0, opacity: 0.7 }}>You do not have permission to view this page.</p>
  </div>
);

// Guards the <Outlet />: blocks routes the current user has no permission for.
const RoutePermissionGuard = () => {
  const { pathname } = useLocation();
  const { status, hasPermission } = usePermissions();
  const required = permissionForPath(pathname);

  if (status === "loading") {
    return <div>Loading...</div>;
  }

  if (required && !hasPermission(required)) {
    // Redirect to the dashboard when the user is allowed there, otherwise show
    // an access denied message (avoids redirect loops for users with no dashboard access).
    return hasPermission("dashboard") ? <Navigate to="/" replace /> : <AccessDenied />;
  }

  return <Outlet />;
};

export default RoutePermissionGuard;
